export const getPrompt = (
  name: string,
  colorsTemplate: string,
  pagesTemplate: string
) => {
  return `#Modern website generation instruction:

You are a professional full-stack web developer, and very skilled web designer. Build a production-ready, modern website from the specifications below. The design is of the most essence - it must be top-notch and award-winning.

**Project Name:** ${name}

---

## 1. Design System

**Colors:**
${colorsTemplate}

**Typography:**
Primary Font: Modern Sans-Serif (Headlines or UI)
Secondary Font: Sans-Serif
A restrained serif only for emphasis (optional)

**Style:** Scandinavian minimal, airy layout, high whitespace (8pt grid). Rounded corners (8px cards, 4px inputs), soft shadows (sm/md only), 1px neutral borders, max-width 1200px. Muted forest green primary, warm off-white background, charcoal text.

**Components:**
- **Buttons:** Primary (solid green), Secondary (outline), Ghost (text). States: hover (scale-102, shadow-md), focus (ring-2 ring-offset-2), active (scale-98), disabled (opacity-40). Sizes: sm/md/lg (h-9 / h-11 / h-12).
- **Cards:** p-6 default (p-8 feature), 8px radius, 1px border, soft shadow. Hover lift (-translateY 2px, shadow-lg, 200ms). Image top, content, CTA.
- **Forms:** Labels above inputs, h-11 minimum, 16px text, real-time validation on blur, inline error text, focus ring-2, loading + success states.
- **Nav:** Desktop sticky header (72px, subtle shadow on scroll). Logo left, links right, CTA button. Mobile hamburger <768px with slide-in drawer.

**Images:** Hero 16:9 editorial ([IMAGE_HERO_HOME]), Product 1:1 ([IMAGE_PRODUCT_X]), Ingredient 4:5 ([IMAGE_INGREDIENT_X]), Feature icons 24x24 line ([ICON_FEATURE_X]), Avatars 1:1 rounded.

---

## 3. Pages & Content

**CRITICAL:** If SEO metadata is provided, use EXACTLY as-is. Do NOT rewrite provided SEO fields.

${pagesTemplate}

**Implementation:** Interpret block title (purpose) + wireframe type (layout) together. Generate professional placeholder content if not provided—avoid "Lorem ipsum", use realistic copy matching the block's intent and {{SEO_KEYWORDS}}.

{{#IF_WIREFRAME_UNSUPPORTED}}
**Fallback:** Use closest equivalent (tabs→accordion, carousel→scroll-grid, map→iframe/static).

---

## 4. Technical Requirements

**Responsive:** Breakpoints: 640/768/1024/1280/1536px. Touch targets 44px minimum.

\`\`\`

Include JSON-LD structured data (Organization, Article, Product, etc.) where applicable.

**robots.txt** (\`/public/robots.txt\`):
\`\`\`
User-agent: *
Allow: /
Sitemap: {{SITE_URL}}/sitemap.xml
\`\`\`

**sitemap.xml** (\`/public/sitemap.xml\`):
\`\`\`xml


{{#EACH_PAGE}}

    {{SITE_URL}}/{{SLUG}}
    {{DATE}}
    weekly
    {{#IF_HOMEPAGE}}1.0{{ELSE}}0.8{{/IF}}

{{/EACH_PAGE}}

\`\`\`

---

## 5. Cookie Consent (GDPR)

**Banner:** Fixed on screen or modal on first visit.

**Content:** "We use cookies to enhance your experience and analyze traffic. By clicking 'Accept All', you consent to our use of cookies. [Learn more](/cookie-policy)"

**Buttons:**
- **Accept All** (primary) → Sets consent=all, dismisses banner, enables analytics
- **Reject Non-Essential** (secondary) → Sets consent=essential-only, dismisses banner

**Storage:** localStorage, 365-day validity, re-prompt on expiry. Check consent before loading analytics:
\`\`\`js
if (getCookieConsent() === 'all') { initAnalytics(); }
\`\`\`

---

## 6. Interactions

**Hover:** Links underline reveal (150ms), Buttons scale-102 + shadow (180ms), Cards lift + shadow-lg, Images zoom within container (scale-105).

**Animations:** Scroll fade-ins (translateY 20px→0, 400ms), stagger 100ms. Subtle micro-interactions. Respect prefers-reduced-motion.

**Forms:** Contact (Name, Email, Message, optional Order #), Newsletter (Email only). Show loading/success/error states. Focus first error on submit.

---

## 7. Content Strategy

**Headlines:** Based on block title + page context. Active voice, benefit-focused.

**Body:** 2-3 paragraphs, incorporate {{SEO_KEYWORDS}} naturally, professional tone matching {{PAGE_INTENT}}.

**CTAs:** Generate variants: "Get Started" / "Start Free Trial" / "Learn More"

**Lists:** 3-6 items, parallel structure, concrete benefits.

---

## 8. Deliverable

Website project with:
- All page routes implemented
- Reusable components in \`/app/components\`

**Verify:**
- All pages responsive (375/768/1280px)
- Navigation functional
- Forms validate
- Images optimized, lazy-loaded
- No console errors
- Cookie banner works
- robots.txt & sitemap.xml accessible

---

**Build functional + accessible first, then polish. Ready to build! 🚀**
  `
}
